import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Paper,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Pagination,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Chat as ChatIcon,
  Close as CloseIcon,
  Person as PersonIcon,
  SmartToy as AIIcon
} from '@mui/icons-material';
import { formatDistance } from 'date-fns';
import { ja } from 'date-fns/locale';

interface ChatSession {
  id: number;
  title: string;
  summary?: string;
  messageCount: number;
  totalTokens: number;
  lastMessageAt?: string;
  isActive: boolean;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  expertAI: {
    id: number;
    name: string;
    description: string;
  };
}

interface ChatMessage {
  id: number;
  content: string;
  isUser: boolean;
  createdAt: string;
  tokensUsed?: number;
}

interface ChatSessionDetail {
  session: {
    id: number;
    title: string;
    expertName: string;
    userName: string;
    createdAt: string;
    updatedAt: string;
    messageCount: number;
    totalTokens: number;
  };
  messages: ChatMessage[];
}

interface UserChatHistoryProps {
  userId: number;
  userName: string;
}

const UserChatHistory: React.FC<UserChatHistoryProps> = ({ userId, userName }) => {
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedSession, setSelectedSession] = useState<ChatSessionDetail | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const fetchChatHistory = async (pageNumber: number = 1) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/admin/users/${userId}/chat-history?page=${pageNumber}&limit=10`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        throw new Error('チャット履歴の取得に失敗しました');
      }

      const result = await response.json();
      if (result.success) {
        setSessions(result.data || []);
        setTotalPages(result.meta?.pagination?.totalPages || 1);
      } else {
        throw new Error(result.message || 'データの取得に失敗しました');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
      setSessions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionMessages = async (sessionId: number) => {
    try {
      setLoadingMessages(true);
      const response = await fetch(`/api/admin/chat-sessions/${sessionId}/messages`, {
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error('メッセージの取得に失敗しました');
      }

      const result = await response.json();
      if (result.success) {
        setSelectedSession(result.data);
        setDialogOpen(true);
      } else {
        throw new Error(result.message || 'メッセージの取得に失敗しました');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    fetchChatHistory(1);
  }, [userId]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
    fetchChatHistory(newPage);
  };

  const handleSessionClick = (sessionId: number) => {
    fetchSessionMessages(sessionId);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSession(null);
  };

  const formatDate = (dateString: string) => {
    return formatDistance(new Date(dateString), new Date(), {
      addSuffix: true,
      locale: ja
    });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
        <ChatIcon sx={{ mr: 1 }} />
        {userName}のチャット履歴
      </Typography>

      <Paper elevation={1}>
        <List>
          {sessions && Array.isArray(sessions) && sessions.map((session) => (
            <React.Fragment key={session.id}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => handleSessionClick(session.id)}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" justifyContent="space-between">
                        <Typography variant="subtitle1">
                          {session.title || `セッション ${session.id}`}
                        </Typography>
                        <Box display="flex" gap={1}>
                          <Chip
                            label={session.expertAI.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {session.isPinned && (
                            <Chip label="固定" size="small" color="secondary" />
                          )}
                        </Box>
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {session.expertAI.description}
                        </Typography>
                        <Box display="flex" alignItems="center" gap={2} mt={1}>
                          <Typography variant="caption">
                            メッセージ: {session.messageCount || 0}件
                          </Typography>
                          <Typography variant="caption">
                            作成: {formatDate(session.createdAt)}
                          </Typography>
                          <Typography variant="caption">
                            更新: {formatDate(session.updatedAt)}
                          </Typography>
                        </Box>
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
              <Divider />
            </React.Fragment>
          ))}
        </List>

        {(!sessions || !Array.isArray(sessions) || sessions.length === 0) && (
          <Box p={3} textAlign="center">
            <Typography color="text.secondary">
              チャット履歴がありません
            </Typography>
          </Box>
        )}

        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
            />
          </Box>
        )}
      </Paper>

      <Dialog 
        open={dialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { height: '80vh' }
        }}
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {selectedSession?.session.title}
            </Typography>
            <IconButton onClick={handleCloseDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
          {selectedSession && (
            <Box mt={1}>
              <Typography variant="subtitle2" color="text.secondary">
                {selectedSession.session.expertName} | {selectedSession.session.userName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                作成: {formatDate(selectedSession.session.createdAt)}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        
        <DialogContent dividers>
          {loadingMessages ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : selectedSession ? (
            <Box>
              {selectedSession.messages.map((message) => (
                <Box
                  key={message.id}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: message.isUser ? '#e3f2fd' : '#f5f5f5',
                    border: message.isUser ? '1px solid #2196f3' : '1px solid #ddd'
                  }}
                >
                  <Box display="flex" alignItems="center" mb={1}>
                    {message.isUser ? (
                      <PersonIcon sx={{ mr: 1, color: '#2196f3' }} />
                    ) : (
                      <AIIcon sx={{ mr: 1, color: '#666' }} />
                    )}
                    <Typography variant="subtitle2" fontWeight="bold">
                      {message.isUser ? 'ユーザー' : 'AI'}
                    </Typography>
                    <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary' }}>
                      {formatDate(message.createdAt)}
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                  {!message.isUser && message.tokensUsed && message.tokensUsed > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      トークン使用量: {message.tokensUsed}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserChatHistory; 